import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import Card from 'components/core/Card';
import Stack from 'components/core/Stack';
import Typography from 'components/core/Typography';
import Grid from 'components/core/Grid';
import Sheet from 'components/core/Sheet';

import InfoLine from './InfoLine';
import DossierActionButtons from './DossierActionButtons';
import DossierPrintButton from './DossierPrintButton';

const EvidenceDossier = ({ evidence }) => {
    const dossierRef = useRef();
    if (!evidence) return null;
    const formatType = evidence.get('evidenceFormat')?.get('filteredFor');
    const dossierSchema = [
        {
            id: 'label',
            label: 'Label',
            value: evidence.get('label')
        },
        {
            id: 'subjects',
            label: 'Subjects',
            value: evidence.get('subjects')?.map(
                subject => `${subject.get('label')} (${subject.get('shortCode')})`
            ).join(', ')
        },
        {
            id: 'evidenceFormat',
            label: 'Format',
            value: evidence.get('evidenceFormat')
        },
        {
            id: 'sourceOwned',
            label: 'Source Owned',
            value: evidence.get('sourceOwned')
        },
    ];

    if (formatType === 'Voucher' && evidence.get('voucher')) {
        dossierSchema.push(
            {
                id: 'repository',
                label: 'Repository',
                value: evidence.get('voucher')?.repository
            },
            {
                id: 'catalogNumber',
                label: 'Catalog Number',
                value: evidence.get('voucher').catalogNumber
            },
            {
                id: 'additionalNumber',
                label: 'Additional Number',
                value: evidence.get('voucher').additionalNumber
            },
            {
                id: 'voucherSource',
                label: 'Voucher Source',
                value: evidence.get('voucher')?.voucherSource
            },
            {
                id: 'event',
                label: 'Event',
                value: evidence.get('voucher')?.event
            }
        );
    } 
    else if (formatType === 'Literature' && evidence.get('literature')) {
        dossierSchema.push(
            {
                id: 'title',
                label: 'Title',
                value: evidence.get('literature').title
            },
            {
                id: 'doi',
                label: 'DOI',
                value: evidence.get('literature').doi
            },
            {
                id: 'publisher',
                label: 'Publisher',
                value: evidence.get('literature').publisher
            },
            {
                id: 'year',
                label: 'Year',
                value: evidence.get('literature').year
            },
            {
                id: 'volume',
                label: 'Volume',
                value: evidence.get('literature').volume
            },
            {
                id: 'issue',
                label: 'Issue',
                value: evidence.get('literature').issue
            },
            {
                id: 'pages',
                label: 'Pages',
                value: `${evidence.get('literature').startPage}-${evidence.get('literature').endPage}`
            },
            {
                id: 'authoredBy',
                label: 'Authored By',
                value: evidence?.get('literature').authoredBy?.map(
                    author => `${author.firstName} ${author.lastName}`
                ).join(', ')
            },
            {
                id: 'event',
                label: 'Event',
                value: evidence.get('literature')?.event
            }
        );
    }
    else if (formatType === 'File' && evidence.get('file')) {
        dossierSchema.push(
            {
                id: 'fileName',
                label: 'File Name',
                value: evidence.get('file').name
            },
            {
                id: 'url',
                label: 'URL',
                value: evidence.get('file').url
            },
            {
                id: 'event',
                label: 'Event',
                value: evidence.get('file')?.event
            }
        );
    }

    return (
        <Sheet 
            ref={dossierRef}
            sx={{
                width: '100%',
                background: 'none',
                mt: 2,
                mb: 2
            }}
        >
            <Card>
                <Grid container>
                    <Grid item xs={12}>
                        <Stack direction='row' spacing={6} alignItems='end' justifyContent='space-between' sx={{mb: 2}}>
                            <Typography variant='h3'>
                                EVIDENCE {evidence.get('label')}
                            </Typography>
                            <Stack direction='row' spacing={1} className="dossier-action-buttons">
                                <DossierPrintButton 
                                    dossierRef={dossierRef} 
                                    title={`Evidence - ${evidence.get('label')}`} 
                                />
                                <DossierActionButtons className="Evidence" id={evidence.id} />
                            </Stack>
                        </Stack>
                    </Grid>
                    {dossierSchema.map(({label, value, id}) => (
                        <InfoLine
                            key={id}
                            id={id}
                            label={label}
                            value={value}
                        />
                    ))}
                </Grid>
            </Card>
        </Sheet>
    );
};

EvidenceDossier.propTypes = {
    evidence: PropTypes.object.isRequired
};

export default EvidenceDossier;